/* eslint-disable object-shorthand */
/* eslint-disable func-names */

import LvLGoalMgr from './LvlGoalMgr';
import LevelEconomyTracker from '../../GameTracking/LevelEconomyTracker';

export default class LvlObject {
  /**
   * A manager of the level goals and points in the game state
   * AKA G.lvl (assigned in Game.js)
   */
  constructor(lvlIndex, lvlData) {
    // config with new way of counting price
    this.failFlowConfig = G.json.settings.failFlowConfig;

    this._state = game.state.getCurrentState(); // Game.js. Apparently persists in global variable and used to access game through strange workarounds
    this._lvlIndex = lvlIndex; // level Index

    this._latestLevel = this._lvlIndex === G.saveState.getLastPassedLevelNr(); // Is this our highest unpassed level? For Mystery gift

    this._coinChanceProb = G.saveState.getStars(this._lvlIndex) === 0 ? 1 : G.json.settings.completedLevelCoinsProb;
    this._stars = 0;
    this._combo = 0;
    this.data = lvlData; // Accessed outside by DDNA
    this._goalAchieved = false;
    this._moves = lvlData.moves;
    this._points = 0;
    this._movesMade = 0;
    this._signalBindings = [];
    this.activateHelpersOverride = false; // A cheat flag to bring up the helpers 2.0

    this.goal = lvlData.goal; // Accessed externally
    this.goalMgr = new LvLGoalMgr(lvlData.goal); // This too
    this._signalBindings.push(this.goalMgr.onGoalAchieved.add(() => {
      this._goalAchieved = true;
      G.sb('onGoalAchieved').dispatch();
    }));

    this._numOfExtraMovesBought = 0; // Previously known as extraMovesBoughtNr
    this._matchNumber = 0;
    this._moneyGained = 0;

    this._comboBonus = G.json.settings.comboBonus;

    this._signalBindings.push(G.sb('onLevelMoneyGain').add((change) => {
      this.moneyGained += change;
    }));
  }

  /**
   * Destroy!
   */
  destroy() {
    if (this._signalBindings) {
      this._signalBindings.forEach((signalBind) => {
        if (signalBind.detach) {
          signalBind.detach();
        }
      });
      this._signalBindings = null;
    }
    this.goalMgr.destroy();
  }

  /**
   * @returns {number}
   */
  get lvlIndex() {
    return this._lvlIndex;
  }

  /**
   * @returns {Board}
   */
  get board() {
    return game.state.getCurrentState().board;
  }

  /**
   * @returns {number}
   */
  get points() {
    return this._points;
  }

  /**
   * Sets the point value
   */
  set points(newVal) {
    this._points = newVal;
  }

  /**
   * @returns {number}
   */
  get moves() {
    return this._moves;
  }

  /**
   * Sets the number of moves left
   */
  set moves(newVal) {
    this._moves = newVal;
  }

  /**
   * Sets the number of stars. Max of 3
   */
  set stars(newVal) {
    this._stars = newVal;
  }

  /**
   * @returns {number}
   */
  get stars() {
    return this._stars;
  }

  /**
   * @returns {number}
   */
  get combo() {
    return this._combo;
  }

  /**
   * @returns {number}
   */
  get coinChanceProb() {
    return this._coinChanceProb;
  }

  /**
   * @returns {boolean}
   */
  get goalAchieved() {
    return this._goalAchieved;
  }

  /**
   * @returns {number}
   */
  get movesMade() {
    return this._movesMade;
  }

  /**
   * @returns {number}
   */
  get moneyGained() {
    return this._moneyGained;
  }

  /**
   * Sets the amount of money gained. Used for DDNA tracking
   */
  set moneyGained(newVal) {
    this._moneyGained = newVal;
  }

  /**
   * @returns {boolean}
   */
  get latestLevel() {
    return this._latestLevel;
  }

  /**
   * @returns {number}
   */
  get numOfExtraMovesBought() {
    return this._numOfExtraMovesBought;
  }

  /**
   * @returns {number}
   */
  getPriceOfExtraMovesForBubble() {
    if (this.failFlowConfig) {
      return this.failFlowConfig.extraMovesStartPrice;
    }
    return this.getPriceOfExtraMoves();
  }

  /**
   * @varreturns {number}
   */
  getPriceOfExtraMoves() {
    if (this.failFlowConfig) {
      return this.failFlowConfig.extraMovesStartPrice + (this._numOfExtraMovesBought * this.failFlowConfig.extraMovesIncreaser);
    }
    return G.json.settings.priceOfExtraMoves + (this._numOfExtraMovesBought * G.json.settings.priceOfExtraMovesIncreaser);
  }

  /**
   * Increments the number of extra moves bought
   * @param {number} price
   */
  markExtraMovesPurchase(price) {
    LevelEconomyTracker.getInstance().onExtraMovesBought(price, this._moves);
    this._numOfExtraMovesBought++;
  }

  /**
   * Tracks the use of preboosters
   * @param {number} nr
   * @param {boolean} isOutOfMovesBooster
   */
  onPreBoosterUse(nr, isOutOfMovesBooster = false) {
    DDNA.missionTracker.onBoosterUse(nr, true, isOutOfMovesBooster);
  }

  /**
   * Tracks the booster used
   * @param {number} nr
   */
  onBoosterUse(nr) {
    DDNA.missionTracker.onBoosterUse(nr, false);
  }

  /**
   * Buys extra moves, reduces amount of coins
   */
  buyExtraMoves() {
    const price = this.getPriceOfExtraMoves();

    G.saveState.changeCoins(-price);

    this._numOfExtraMovesBought++;
    this.changeMoveNumber(5);
    G.sb('onExtraMovesUsed').dispatch();
  }

  /**
   * Is goal achieved?
   * @returns {boolean}
   */
  isGoalAchieved() {
    return this._goalAchieved;
  }

  /**
   * Things updating when moves are made
   */
  madeMove() {
    if (!G.IMMEDIATE) {
      this.changeMoveNumber(-1);
    }
    if (!this._goalAchieved) {
      this._movesMade++;
      G.sb('userMadeMove').dispatch();
    }
    G.sb('madeMove').dispatch();
  }

  /**
   * Number of moves are updated
   * @param {number} change
   */
  changeMoveNumber(change) {
    this._moves += change;
    G.sb('changeMoveNumber').dispatch();
  }

  /**
   * Number of points are updated
   * @param {number} change
   */
  changePointsNumber(change) {
    this._points += change;
    G.sb('onPointsAdded').dispatch(change);
    G.sb('onPointsChange').dispatch(this._points);
  }

  /**
   * Combo count increases
   */
  increaseCombo() {
    this._combo++;
    G.sb('onComboIncrease').dispatch(this._combo);
  }

  /**
   * Combo breaker
   */
  endCombo() {
    this._combo = 0;
    G.sb('onComboBreak').dispatch();
  }

  /**
   * Match happens, points are flying, some tracking happens
   * @param {number} amount
   * @param {number} meanX
   * @param {number} meanY
   * @param {number} color
   */
  processMatch(amount, meanX, meanY, color) {
    const pointsToAdd = amount * (10 + this.getComboBonus());
    this.changePointsNumber(pointsToAdd);
    const pxOut = this._state.board.cellToPxOut([meanX, meanY]);
    G.sb('displayPoints').dispatch(pxOut[0], pxOut[1], pointsToAdd, color);

    this._matchNumber++;

    if (this._lvlIndex === 0 && this._state.tut && !this._state.tut.skipped) {
      switch (this._matchNumber) {
        case 1:
          DDNA.tracking.ftuxEvent(4, 'firstMatch');
          OMT.platformTracking.logFTUEvent('FTUFirstMatch');
          break;
        case 2:
          // no longer tracking this event
          break;
        case 3:
          // no longer tracking this event
          break;
        default: break;
      }
    }
  }

  /**
   * The bonus points you get for having a combo
   * @returns {number}
   */
  getComboBonus() {
    return this._comboBonus[Math.min(this._combo, this._comboBonus.length - 1)];
  }

  /**
   * Is this type a goal?
   * @param {string} type
   */
  isGoal(type) {
    return this.goalMgr.isGoal(type);
  }
}
